import { ErrorMessage, Field, Form, Formik } from "formik";
import { Modal } from "react-bootstrap";
import { FaTimes } from "react-icons/fa";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { loginWithOTP, verifyOtp } from "../../features/user/userSlice";
import { useState, useEffect, useContext } from "react";
import { CurrencyContext } from "../../features/context/CurrencyContext";
import { base_url } from "../../utils/axiosConfig";
import { useNavigate } from "react-router-dom";

const MemberSignIn = ({ show, onHide }) => {
  const [otpRequested, setOtpRequested] = useState(false);
  const [packageBuy, setPackageBuy] = useState(false);
  const [packageData, setPackageData] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const { convertPrice, getCurrencySymbol } = useContext(CurrencyContext);

  const fetchPackageData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${base_url}v1/package/fetchAllpackage`);
      const data = await response.json();
      setPackageData(data.data);
    } catch (error) {
      console.error("Error fetching Package:", error);
      setError("Failed to load packages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (packageBuy) {
      fetchPackageData();
    }
  }, [packageBuy]);

  const getSigninSchema = (otpRequested, packageBuy) => {
    if (packageBuy) {
      return Yup.object().shape({
        email: Yup.string()
          .required("Email is Required")
          .email("Email Should be valid"),
      });
    }

    return Yup.object().shape({
      email: Yup.string()
        .required("Email is Required")
        .email("Email Should be valid"),
      otp: Yup.string().when([], {
        is: () => otpRequested,
        then: () =>
          Yup.string()
            .required("OTP is Required")
            .matches(/^\d{6}$/, "OTP must be 6 digits"),
      }),
    });
  };

  const handleSelectPackage = (packageId, setFieldValue) => {
    setSelectedPackage(packageId);
    setFieldValue("packageId", packageId);
  };

  const handleSubmit = async (values, { resetForm }) => {
    setLoading(true);

    if (!otpRequested) {
      try {
        const response = await dispatch(loginWithOTP(values.email)).unwrap();
        setOtpRequested(true);
        setPackageBuy(response.packageBuy);

        if (response.paymentSessionId) {
          const cashfree = new window.Cashfree();
          cashfree.checkout({
            paymentSessionId: response.paymentSessionId,
            redirectTarget: "_self",
            mode: "production",
          });
        }

        if (!response.packageBuy) {
          toast.success("OTP sent to your email.");
        }
      } catch (error) {
        toast.error("Failed to send OTP.");
      } finally {
        setLoading(false);
      }
    } else if (packageBuy && selectedPackage) {
      try {
        const formData = new FormData();
        formData.append("email", values.email);
        formData.append("packageId", values.packageId);

        const response = await fetch(`${base_url}user/selectPackage`, {
          method: "POST",
          body: formData,
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message);
        }

        const cashfree = new window.Cashfree();
        cashfree.checkout({
          paymentSessionId: data.paymentSessionId,
          redirectTarget: "_self",
          mode: "production",
        });

        resetForm();
        onHide();
        toast.success("Redirecting to payment...");
      } catch (error) {
        toast.error("Payment failed. Try again.");
      } finally {
        setLoading(false);
      }
    } else {
      try {
        await dispatch(
          verifyOtp({ email: values.email, otp: values.otp }),
        ).unwrap();

        resetForm();
        onHide();
        toast.success("Sign in successful!");
        window.open("https://lifeonplus.in/dashboardPanel/");
      } catch (error) {
        toast.error("Invalid OTP.");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Modal
      show={show}
      dialogClassName={packageBuy ? "max-w-6xl" : "max-w-lg"}
      onHide={onHide}
      centered
      backdrop="static"
    >
      <Modal.Body className="p-6">
        <div className="relative bg-white rounded-lg">
          {/* Close Button */}
          <button
            onClick={onHide}
            className="absolute top-2 right-2 bg-transparent border-0"
          >
            <FaTimes size={16} color="#666" />
          </button>

          {/* Header */}
          <div className="text-center mb-4">
            <img src="/lifeon.svg" alt="logo" className="mx-auto h-20 mb-2" />

            <h3 className="text-2xl font-bold bg-gradient-to-r from-[#00C70D] to-[#005AD0] bg-clip-text text-transparent">
              Member Sign In
            </h3>

            <p className="text-gray-500 text-sm">
              {packageBuy
                ? "Select a Package"
                : "Sign in with your email and OTP"}
            </p>
          </div>

          <Formik
            initialValues={{
              email: "",
              otp: "",
              packageId: "",
            }}
            validationSchema={getSigninSchema(otpRequested, packageBuy)}
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue }) => (
              <Form className="flex flex-col gap-4">
                {/* EMAIL */}
                {!packageBuy && (
                  <div>
                    <label className="font-semibold text-sm">Email</label>

                    <Field
                      type="email"
                      name="email"
                      placeholder="Enter Email"
                      className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100 rounded"
                      disabled={otpRequested || loading}
                    />

                    <ErrorMessage
                      name="email"
                      component="div"
                      className="text-red-500 text-xs"
                    />
                  </div>
                )}

                {/* OTP */}
                {otpRequested && !packageBuy && (
                  <div>
                    <label className="font-semibold text-sm">OTP</label>

                    <Field
                      type="text"
                      name="otp"
                      placeholder="Enter 6-digit OTP"
                      className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100 rounded"
                      disabled={loading}
                    />

                    <ErrorMessage
                      name="otp"
                      component="div"
                      className="text-red-500 text-xs"
                    />
                  </div>
                )}

                {/* PACKAGES */}
                {packageBuy && (
                  <div className="py-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                      {packageData?.map((plan, index) => (
                        <div
                          key={index}
                          onClick={() =>
                            !loading &&
                            handleSelectPackage(plan._id, setFieldValue)
                          }
                          className={`p-4 rounded-lg shadow-md cursor-pointer flex flex-col transition
                          ${
                            selectedPackage === plan._id
                              ? "border-2 border-[#FF8500] bg-orange-50"
                              : "border border-gray-200 hover:border-[#FF8500]"
                          }`}
                        >
                          <img
                            src={plan?.images?.[0] || "/no-image.png"}
                            alt={plan.name}
                            className="img-fluid rounded mb-2"
                            style={{
                              height: "200px",
                              // width: "100%",
                              objectFit: "cover",
                            }}
                          />

                          <h3 className="font-bold text-lg mb-2">
                            {plan.name}
                          </h3>

                          <p className="text-sm font-medium">Start At</p>

                          <div className="flex items-baseline mb-3">
                            <span className="text-xl font-bold">
                              {getCurrencySymbol()}
                              {convertPrice(plan?.price?.totalAmount)}
                            </span>

                            <span className="ml-2 text-sm text-gray-500">
                              /Month • {plan?.duration_days || "N/A"} days
                            </span>
                          </div>

                          <p className="text-sm font-medium mb-3">
                            {plan.description}
                          </p>
                          <p className="text-muted">
                            SAC/HSN Code:{" "}
                            <strong>{plan.hsnCode || "N/A"}</strong>
                          </p>
                          <div className="h-40 overflow-y-auto space-y-2 border-t pt-3 mb-3">
                            {plan?.features?.map((feature, i) => (
                              <div key={i} className="flex gap-2">
                                <span className="text-green-500">✔</span>
                                <span className="text-sm">{feature}</span>
                              </div>
                            ))}
                          </div>

                          {selectedPackage === plan._id && (
                            <span className="text-center text-green-600 text-sm font-semibold">
                              Selected
                            </span>
                          )}
                        </div>
                      ))}
                    </div>

                    {error && (
                      <div className="text-red-500 text-sm mt-4">{error}</div>
                    )}

                    <div className="flex justify-center mt-8">
                      <button
                        type="submit"
                        disabled={!selectedPackage || loading}
                        className="px-10 py-3 bg-gradient-to-r from-[#00C70D] to-[#005AD0] text-white rounded-lg font-semibold disabled:opacity-50 flex items-center gap-2"
                      >
                        {loading && (
                          <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                        )}
                        {loading ? "Processing..." : "Submit Payment"}
                      </button>
                    </div>
                  </div>
                )}

                {/* OTP BUTTON */}
                {!packageBuy && (
                  <button
                    type="submit"
                    disabled={
                      loading || !values.email || (otpRequested && !values.otp)
                    }
                    className="w-full py-2 bg-gradient-to-r from-[#00C70D] to-[#005AD0] text-white rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {loading && (
                      <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                    )}

                    {loading
                      ? "Processing..."
                      : otpRequested
                        ? "Verify OTP"
                        : "Request OTP"}
                  </button>
                )}
              </Form>
            )}
          </Formik>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default MemberSignIn;
